import React, { useMemo } from 'react';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiBluetooth,
    FiPower,
    FiSearch,
    FiRefreshCw,
    FiLock,
    FiUnlock,
    FiLink,
    FiLink2,
    FiPlusCircle,
} from 'react-icons/fi';

import { db } from '../../firebase';

// Bluetooth tab for the device detail page.
//
// The monitoring app on the device exposes Bluetooth over the shared `command`
// field (bluetooth:on|off|lock_on|lock_off|list|scan|pair:<ADDR>|
// connect:<ADDR>|disconnect:<ADDR>) and mirrors the live state under
// mdm/devices/<MAC>/bluetooth/{enabled,lock_on,paired[],discovered[]}. We read
// that state and send those commands; the app applies them and pushes back an
// updated list, so this view is fully live.
//
// Reality note shown to the operator: the first pairing to a NEW device (e.g. a
// car stereo) needs a one-time confirmation ON the device — a plain Device Owner
// cannot pair silently — after which Android auto-reconnects on its own.

export default function BluetoothTab({ mac, device }) {
    const bt = device?.bluetooth || {};
    const enabled = !!bt.enabled;
    const lockOn = !!bt.lock_on;

    const paired = useMemo(() => normalizeList(bt.paired), [bt.paired]);
    const discovered = useMemo(() => normalizeList(bt.discovered), [bt.discovered]);

    const send = async (command, label) => {
        try {
            await update(ref(db, `mdm/devices/${mac}`), { command });
            toast.success(`${label} requested`);
        } catch (err) {
            toast.error(`${label} failed: ${err.message}`);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* ---- Controls ------------------------------------------------ */}
            <section className="card-chrome p-5 shadow-sm">
                <header className="mb-4 flex items-center justify-between">
                    <h2 className="inline-flex items-center gap-2 text-base font-semibold text-brand-400">
                        <FiBluetooth /> Bluetooth
                    </h2>
                    <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            enabled
                                ? 'bg-green-500/15 text-green-400'
                                : 'bg-white/[0.06] text-gray-400'
                        }`}
                    >
                        {enabled ? 'On' : 'Off'}
                    </span>
                </header>

                <div className="grid grid-cols-2 gap-2">
                    <ActionTile
                        onClick={() => send('bluetooth:on', 'Turn on')}
                        icon={<FiPower />}
                        label="Power"
                        hint="Turn Bluetooth on"
                    />
                    <ActionTile
                        onClick={() => send('bluetooth:off', 'Turn off')}
                        icon={<FiPower />}
                        label="Power"
                        hint="Turn Bluetooth off"
                        tone="danger"
                    />
                    <ActionTile
                        onClick={() => send('bluetooth:scan', 'Scan')}
                        icon={<FiSearch />}
                        label="Discover"
                        hint="Scan for nearby devices"
                    />
                    <ActionTile
                        onClick={() => send('bluetooth:list', 'Refresh')}
                        icon={<FiRefreshCw />}
                        label="Refresh"
                        hint="Reload paired list"
                    />
                </div>

                <Divider className="my-4" />

                {/* Keep-on lock */}
                <div className="card-inset p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                            {lockOn ? <FiLock className="text-brand-400" /> : <FiUnlock className="text-brand-400" />}
                            Keep Bluetooth on
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                            {lockOn
                                ? 'Locked on — the device re-enables it if it ever turns off (taxi car-audio)'
                                : 'Not enforced — Bluetooth can be turned off on the device'}
                        </div>
                    </div>
                    <button
                        onClick={() =>
                            lockOn
                                ? send('bluetooth:lock_off', 'Unlock')
                                : send('bluetooth:lock_on', 'Lock on')
                        }
                        aria-pressed={lockOn}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                            lockOn
                                ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                                : 'bg-white/[0.05] text-gray-300 hover:bg-white/[0.10]'
                        }`}
                    >
                        {lockOn ? 'Locked on' : 'Off'}
                    </button>
                </div>

                <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                    First pairing to a new device (e.g. a car stereo) is confirmed once on the
                    device itself; afterwards Android reconnects automatically.
                </p>
            </section>

            {/* ---- Paired -------------------------------------------------- */}
            <section className="card-chrome p-5 shadow-sm">
                <header className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-brand-400">Paired devices</h2>
                    <span className="text-xs text-gray-500">{paired.length}</span>
                </header>

                {paired.length === 0 ? (
                    <EmptyRow text="No paired devices. Pair one on the device, or scan below." />
                ) : (
                    <ul className="space-y-2">
                        {paired.map((d) => (
                            <li
                                key={d.address}
                                className="card-inset p-3 flex items-center justify-between gap-3"
                            >
                                <DeviceIdentity name={d.name} address={d.address}>
                                    {d.connected && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-500/15 text-green-400">
                                            connected
                                        </span>
                                    )}
                                </DeviceIdentity>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <RowButton
                                        onClick={() => send(`bluetooth:connect:${d.address}`, 'Connect')}
                                        icon={<FiLink />}
                                        label="Connect"
                                    />
                                    <RowButton
                                        onClick={() => send(`bluetooth:disconnect:${d.address}`, 'Disconnect')}
                                        icon={<FiLink2 />}
                                        label="Disconnect"
                                        tone="muted"
                                    />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            {/* ---- Discovered --------------------------------------------- */}
            <section className="card-chrome p-5 shadow-sm lg:col-span-2">
                <header className="mb-4 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-brand-400">Nearby devices</h2>
                    <span className="text-xs text-gray-500">
                        {discovered.length ? `${discovered.length} found` : 'run a scan'}
                    </span>
                </header>

                {discovered.length === 0 ? (
                    <EmptyRow text='Press "Discover" to scan for nearby Bluetooth devices (~12s).' />
                ) : (
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {discovered.map((d) => (
                            <li
                                key={d.address}
                                className="card-inset p-3 flex items-center justify-between gap-3"
                            >
                                <DeviceIdentity name={d.name} address={d.address}>
                                    {typeof d.rssi === 'number' && <SignalBadge rssi={d.rssi} />}
                                </DeviceIdentity>
                                <RowButton
                                    onClick={() => send(`bluetooth:pair:${d.address}`, 'Pair')}
                                    icon={<FiPlusCircle />}
                                    label="Pair"
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Small presentational helpers (kept local; mirror ControlsTab's language).
// ---------------------------------------------------------------------------

function normalizeList(value) {
    // RTDB can hand us an array, or an object keyed by index, or null.
    if (!value) return [];
    const arr = Array.isArray(value) ? value : Object.values(value);
    return arr.filter((d) => d && d.address);
}

function DeviceIdentity({ name, address, children }) {
    return (
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white truncate">
                    {name && name !== '(unknown)' ? name : 'Unknown device'}
                </span>
                {children}
            </div>
            <div className="text-xs font-mono text-gray-500 truncate">{address}</div>
        </div>
    );
}

function SignalBadge({ rssi }) {
    // -50 great, -70 ok, -90 weak. Colour by band.
    const tone =
        rssi >= -60
            ? 'bg-green-500/15 text-green-400'
            : rssi >= -80
            ? 'bg-yellow-500/15 text-yellow-400'
            : 'bg-white/[0.06] text-gray-400';
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${tone}`}>
            {rssi} dBm
        </span>
    );
}

function ActionTile({ onClick, icon, label, hint, tone = 'neutral' }) {
    const tones = {
        neutral: { text: 'text-gray-400', hover: 'hover:bg-white/[0.06]' },
        danger: { text: 'text-red-400', hover: 'hover:bg-red-500/10 hover:border-red-500/40' },
    };
    const c = tones[tone] || tones.neutral;
    return (
        <button
            onClick={onClick}
            className={`bg-surface-inset border border-white/[0.08] rounded-lg p-4 text-left transition-colors ${c.hover}`}
        >
            <div className={`inline-flex items-center gap-1.5 ${c.text} text-xs uppercase tracking-wider`}>
                {icon} {label}
            </div>
            <div className="mt-1 text-sm font-medium text-white">{hint}</div>
        </button>
    );
}

function RowButton({ onClick, icon, label, tone = 'brand' }) {
    const cls =
        tone === 'muted'
            ? 'bg-white/[0.05] text-gray-300 hover:bg-white/[0.10]'
            : 'bg-brand/15 text-brand-400 hover:bg-brand/25';
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition ${cls}`}
        >
            {icon} {label}
        </button>
    );
}

function EmptyRow({ text }) {
    return (
        <div className="card-inset p-4 text-sm text-gray-500 text-center">{text}</div>
    );
}

function Divider({ className = '' }) {
    return <div className={`h-px bg-white/[0.06] ${className}`} />;
}
